import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { LEAVE_LABEL, cnDate, fmtDateWeek, fmtMonthDay, type PersonDTO, type PersonDayDTO, type RadarProjectDTO, type Role } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { ApiError, errorText } from '../../lib/api';
import { timeCN, todayCN } from '../../lib/clock';
import { useLayout } from '../../lib/media';
import { usePerson } from '../../lib/queries';
import { Icon, StatusIcon } from '../../ui/icons';
import { Avatar, Button, EmptyState, Skeleton } from '../../ui/primitives';
import './person.css';

// 成员页：所有人能看到基本信息和项目；完整日报只有本人、直属主管、本团队的主管、管理员能看；
// “建议关注”只给直属主管（设计稿 16a 可见范围）。

const ROLE_LABEL: Record<Role, string> = { admin: '管理员', manager: '主管', member: '成员' };

function statusLine(p: PersonDTO): string | null {
  const m = p.member;
  const today = todayCN();
  if (m.status === 'left') return '已离职 · 历史日报保留在项目动态里';
  if (m.status === 'offboarding') return `离职交接中${m.offboardDate ? ` · 最后工作日 ${fmtDateWeek(m.offboardDate)}` : ''}`;
  if (p.leave) {
    const label = LEAVE_LABEL[p.leave.type];
    const range = p.leave.from === p.leave.to ? fmtMonthDay(p.leave.from) : `${fmtMonthDay(p.leave.from)} – ${fmtMonthDay(p.leave.to)}`;
    return p.leave.from <= today ? `今天${label} · ${range}` : `${range} ${label}`;
  }
  return null;
}

export function PersonPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'mobile';
  const { data: p, isLoading, error } = usePerson(id);

  if (error) {
    return (
      <EmptyState icon="user" title={error instanceof ApiError && error.status === 404 ? '没找到这个人' : '成员页没有加载出来'} desc={errorText(error)}>
        <Button onClick={() => navigate(-1)}>返回</Button>
      </EmptyState>
    );
  }
  if (isLoading || !p) {
    return (
      <div style={{ padding: 40 }} aria-busy="true">
        <Skeleton w={44} h={44} style={{ borderRadius: '50%' }} />
        <Skeleton w={160} h={20} style={{ marginTop: 14 }} />
      </div>
    );
  }

  const m = p.member;
  const status = statusLine(p);
  const done = p.owned.filter((x) => x.status === 'done');
  const active = p.owned.filter((x) => x.status !== 'done');

  const main = (
    <div className="ps-in">
      <div className="ps-head">
        <Avatar initial={m.initial} seed={m.name} size={44} />
        <div className="col" style={{ gap: 4, minWidth: 0 }}>
          <h1 className="t-page-title">{m.name}</h1>
          <span className="t-meta">{[m.teamName, m.title !== m.teamName ? m.title : null, m.responsibility].filter(Boolean).join(' · ') || ROLE_LABEL[m.role]}</span>
        </div>
      </div>
      {status && (
        <div className={clsx('ps-status', m.status === 'left' && 'left')}>
          <Icon name={m.status === 'left' || m.status === 'offboarding' ? 'out' : p.leave?.type === 'trip' ? 'plane' : 'sun'} size={14} />
          <span>{status}</span>
        </div>
      )}

      {p.insight && (
        <button type="button" className="ps-insight" onClick={() => navigate(`/team?i=${p.insight!.id}`)}>
          <span className="k">
            <Icon name="lock" size={12} stroke={1.4} />
            本周建议关注 · {p.insight.kind === 'talk' ? '建议沟通' : '值得认可'}
            {p.insight.status !== 'open' ? ` · ${p.insight.status === 'talked' ? '已沟通' : p.insight.status === 'recognized' ? '已认可' : '已移出'}` : ''}
          </span>
          <span className="v">{p.insight.headline}</span>
          <span className="t-meta">仅你可见 · 在团队状态中查看 ›</span>
        </button>
      )}

      <Section title="负责的项目" count={active.length}>
        {active.length === 0 && <div className="ps-empty">没有负责中的项目</div>}
        {active.map((x) => (
          <ProjectRow key={x.id} p={x} />
        ))}
        {done.length > 0 && <div className="ps-sub">已完成 {done.map((x) => `${x.code} ${x.name}`).join('、')}</div>}
      </Section>

      {p.participating.length > 0 && (
        <Section title="参与的项目" count={p.participating.length}>
          {p.participating.map((x) => (
            <ProjectRow key={x.id} p={x} />
          ))}
        </Section>
      )}

      {p.days ? (
        <Section title="最近 10 个工作日" hint={p.isSelf ? (p.manager ? '你的主管看到的也是这些' : '只有你和管理员能看到') : '只有本人、直属主管和管理员能看到'}>
          {!p.expectsReports && p.days.every((d) => !d.items.length) ? (
            <div className="ps-empty">没有参与中的项目，不需要写日报。</div>
          ) : (
            <div className="ps-days">
              {p.days.map((d) => (
                <Day key={d.date} d={d} />
              ))}
            </div>
          )}
        </Section>
      ) : (
        <Section title="日报">
          <div className="ps-locked">
            <Icon name="lock" size={14} />
            <span>
              完整日报只有本人、直属主管和管理员能看。{m.name}写到项目里的进展，在各项目的动态里能看到。
            </span>
          </div>
        </Section>
      )}
    </div>
  );

  const panel = (
    <aside className={clsx('ps-panel', mobile && 'mobile')}>
      <div className="ps-kvs">
        <KV k="状态">{m.status === 'left' ? '已离职' : m.status === 'offboarding' ? '离职交接中' : p.leave && p.leave.from <= todayCN() ? LEAVE_LABEL[p.leave.type] + '中' : '在岗'}</KV>
        <KV k="团队">{m.teamName ?? '—'}</KV>
        <KV k="角色">{ROLE_LABEL[m.role]}</KV>
        <KV k="直属主管">
          {p.manager ? (
            <button type="button" className="ps-person" onClick={() => navigate(`/people/${p.manager!.id}`)}>
              <Avatar initial={p.manager.initial} seed={p.manager.name} />
              {p.manager.name}
            </button>
          ) : (
            '—'
          )}
        </KV>
        {p.reports.length > 0 && (
          <KV k="下属" top>
            <span className="col" style={{ gap: 2 }}>
              {p.reports.map((r) => (
                <button key={r.id} type="button" className="ps-person" onClick={() => navigate(`/people/${r.id}`)}>
                  <Avatar initial={r.initial} seed={r.name} />
                  {r.name}
                  {r.teamName && <span className="t-meta">{r.teamName}</span>}
                </button>
              ))}
            </span>
          </KV>
        )}
        {m.email && <KV k="工作邮箱">{m.email}</KV>}
        {m.wechatBound !== null && <KV k="微信">{m.wechatBound ? '已绑定' : '未绑定'}</KV>}
        <KV k="加入">{fmtMonthDay(cnDate(new Date(m.joinedAt)))}</KV>
      </div>
      {p.canEdit && (
        <Button mobile={mobile} onClick={() => navigate(`/settings/members?edit=${m.id}`)}>
          编辑成员信息
        </Button>
      )}
      {p.isSelf && (
        <Button mobile={mobile} onClick={() => navigate('/me')}>
          请假、负责的项目
        </Button>
      )}
    </aside>
  );

  if (mobile) {
    return (
      <div className="ps-m">
        <button type="button" className="ps-m-back" onClick={() => navigate(-1)}>
          <Icon name="chevL" size={18} color="var(--text-3)" />
          <span>返回</span>
        </button>
        <div className="ps-m-scroll">
          {main}
          {panel}
        </div>
      </div>
    );
  }

  return (
    <>
      <TopBar crumbs={['人员', m.teamName ?? m.title ?? '未分配团队', m.name]} />
      <div className="ps">
        <div className="ps-main">{main}</div>
        {layout === 'full' || layout === 'noPanel' ? panel : null}
      </div>
    </>
  );
}

function Section({ title, count, hint, children }: { title: string; count?: number; hint?: string; children: ReactNode }) {
  return (
    <section className="ps-sec">
      <div className="ps-sec-head">
        <span className="name">{title}</span>
        {count !== undefined && <span>{count}</span>}
        {hint && <span className="flex1" style={{ textAlign: 'right' }}>{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function KV({ k, top, children }: { k: string; top?: boolean; children: ReactNode }) {
  return (
    <div className={clsx('ps-kv', top && 'top')}>
      <span className="k">{k}</span>
      <span className="v">{children}</span>
    </div>
  );
}

function ProjectRow({ p }: { p: RadarProjectDTO }) {
  const navigate = useNavigate();
  const signal = p.health.find((h) => h.kind !== 'owner_on_leave');
  return (
    <button type="button" className="ps-proj" onClick={() => navigate(`/projects/${p.id}`)}>
      <StatusIcon status={p.status} />
      <span className="code">{p.code}</span>
      <span className="name">{p.name}</span>
      <span className="health">{signal?.label ?? ''}</span>
      <span className="due">{p.dueDate ? fmtMonthDay(p.dueDate) : '—'}</span>
    </button>
  );
}

const STATE_LABEL = (d: PersonDayDTO) => {
  switch (d.state) {
    case 'submitted':
      return d.submittedAt ? `${timeCN(d.submittedAt)} 提交` : '已提交';
    case 'late':
      return d.submittedAt ? `${timeCN(d.submittedAt)} 提交 · 过了截止时间` : '截止后提交';
    case 'revising':
      return '撤回修改中 · 下面是上一次提交的版本';
    case 'on_leave':
      return d.leaveType ? LEAVE_LABEL[d.leaveType] : '请假';
    case 'missing':
      return '没有提交';
    case 'pending':
      return d.date === todayCN() ? '今天还没提交' : '没写';
  }
};

function Day({ d }: { d: PersonDayDTO }) {
  const [plans, setPlans] = useState(false);
  return (
    <div className={clsx('ps-day', (d.state === 'missing' || d.state === 'on_leave' || d.state === 'pending') && 'empty')}>
      <div className="ps-day-head">
        <span className="d">{fmtDateWeek(d.date)}</span>
        <span className={clsx('s', d.state === 'missing' && 'warn')}>{STATE_LABEL(d)}</span>
      </div>
      {d.items.map((i, k) => (
        <div key={k} className="ps-item">
          <span className="code">{i.code}</span>
          <span className={clsx('t', i.noProgress && 'muted')}>{i.noProgress ? `今天没有进展${i.text.trim() ? `：${i.text}` : ''}` : i.text}</span>
        </div>
      ))}
      {d.note && (
        <div className="ps-item">
          <span className="code">补充</span>
          <span className="t">{d.note}</span>
        </div>
      )}
      {d.plans.length > 0 && (
        <>
          <button type="button" className="ps-plans-toggle" onClick={() => setPlans(!plans)}>
            <Icon name={plans ? 'chevD' : 'chevR'} size={12} />
            明日计划 {d.plans.length} 项
          </button>
          {plans &&
            d.plans.map((pl, k) => (
              <div key={k} className="ps-item plan">
                <span className="code">{pl.code ?? ''}</span>
                <span className="t">{pl.text}</span>
              </div>
            ))}
        </>
      )}
    </div>
  );
}
